import React, { useEffect, useState } from 'react';
import api from '../utils/api';

const CATEGORY_COLORS = {
  logistics:  { color: 'var(--neon-cyan)',   label: 'Logistics' },
  packaging:  { color: 'var(--neon-orange)', label: 'Packaging' },
  energy:     { color: 'var(--neon-yellow)', label: 'Energy' },
  supplier:   { color: 'var(--neon-green)',  label: 'Supplier' },
};
const GRADE_COLORS = { A: 'var(--neon-green)', B: 'var(--neon-cyan)', C: 'var(--neon-yellow)', D: 'var(--neon-pink)' };
const VISIBILITY_LABELS = { full: '✓ Shares emissions data', partial: '~ Partial data', none: 'No disclosure' };
const VISIBILITY_COLORS  = { full: 'var(--neon-green)', partial: 'var(--neon-yellow)', none: 'var(--muted2)' };

export default function Vendors() {
  const [vendors, setVendors]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch]           = useState('');
  const [compareIds, setCompareIds]   = useState([]);
  const [sendingInquiry, setSendingInquiry] = useState(null);
  const [inquiryDone, setInquiryDone] = useState({});

  useEffect(() => {
    api.get('/vendors')
      .then((r) => setVendors(r.data.vendors || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Pick category from URL ?category= param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get('category');
    if (cat) setCategoryFilter(cat);
  }, []);

  const filtered = vendors.filter((v) => {
    if (categoryFilter !== 'all' && v.category !== categoryFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return v.name?.toLowerCase().includes(s) || v.tagline?.toLowerCase().includes(s);
    }
    return true;
  });

  const toggleCompare = (id) => {
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 2 ? [...prev, id] : prev
    );
  };

  const handleInquiry = async (vendorId) => {
    setSendingInquiry(vendorId);
    try {
      await api.post('/vendors/inquiry', { vendor_id: vendorId });
      setInquiryDone((prev) => ({ ...prev, [vendorId]: true }));
    } catch {}
    setSendingInquiry(null);
  };

  const compareVendors = compareIds.map((id) => vendors.find((v) => v.id === id)).filter(Boolean);

  const categories = ['all', ...Object.keys(CATEGORY_COLORS)];
  const counts = vendors.reduce((acc, v) => { acc[v.category] = (acc[v.category] || 0) + 1; return acc; }, {});

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Vendor Hub — Vendor Marketplace</div>
        <h1>Low-Carbon Vendor Directory</h1>
        <p className="section-copy">Discover verified green vendors across logistics, packaging, energy, and supply chain. Compare emissions grades and register interest.</p>
      </section>

      {/* Filters */}
      <section className="surface-panel" style={{ padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {categories.map((cat) => (
            <button key={cat} type="button"
              className={`btn btn-sm ${categoryFilter === cat ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setCategoryFilter(cat)}
              style={{ padding: '0.25rem 0.85rem' }}>
              {cat === 'all' ? `All vendors (${vendors.length})` : `${CATEGORY_COLORS[cat]?.label} (${counts[cat] || 0})`}
            </button>
          ))}
          <input
            className="form-input"
            type="text"
            placeholder="Search vendors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginLeft: 'auto', maxWidth: '240px', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
          />
        </div>
      </section>

      {/* Vendor Grid */}
      {loading ? (
        <div className="stat-sub" style={{ padding: '2rem' }}>Loading vendors…</div>
      ) : filtered.length === 0 ? (
        <div className="surface-panel" style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="stat-sub">No vendors found for this filter.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {filtered.map((v) => {
            const catMeta = CATEGORY_COLORS[v.category] || {};
            const gradeColor = GRADE_COLORS[v.emissions_grade] || 'var(--muted2)';
            const isComparing = compareIds.includes(v.id);
            return (
              <div key={v.id} className="surface-panel listing-card glow-surface" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', border: isComparing ? `1px solid var(--neon-cyan)` : undefined }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span className="badge" style={{ color: catMeta.color, borderColor: catMeta.color, fontSize: '0.6rem' }}>{catMeta.label}</span>
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    {v.is_verified && <span className="badge" style={{ color: 'var(--neon-green)', borderColor: 'var(--neon-green)', fontSize: '0.55rem' }}>✓ Verified</span>}
                    <span className="badge" style={{ color: gradeColor, borderColor: gradeColor, fontSize: '0.65rem', fontFamily: 'Space Mono, monospace' }}>Grade {v.emissions_grade}</span>
                  </div>
                </div>

                {/* Name & tagline */}
                <div>
                  <div className="listing-title" style={{ fontSize: '1rem', marginBottom: '0.2rem' }}>{v.name}</div>
                  <div className="listing-copy">{v.tagline}</div>
                </div>

                {/* CO2e highlight */}
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.4rem', color: 'var(--neon-green)' }}>
                  -{v.emission_reduction_pct}%
                  <span style={{ fontSize: '0.7rem', color: 'var(--muted2)', marginLeft: '0.4rem' }}>less CO2e</span>
                </div>

                {/* Chips */}
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {(v.certifications || []).map((c) => (
                    <span key={c} className="badge" style={{ fontSize: '0.58rem', color: 'var(--muted2)', borderColor: 'var(--border)' }}>{c}</span>
                  ))}
                  <span className="badge" style={{ fontSize: '0.58rem', color: VISIBILITY_COLORS[v.scope3_visibility], borderColor: VISIBILITY_COLORS[v.scope3_visibility] }}>
                    {VISIBILITY_LABELS[v.scope3_visibility]}
                  </span>
                </div>

                {/* Pricing */}
                {v.pricing_model && (
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', color: 'var(--muted2)' }}>
                    Pricing: {v.pricing_model.replace('_', ' ')}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    style={{ flex: 1, fontSize: '0.72rem' }}
                    disabled={sendingInquiry === v.id || inquiryDone[v.id]}
                    onClick={() => handleInquiry(v.id)}>
                    {inquiryDone[v.id] ? '✓ Interest registered' : sendingInquiry === v.id ? 'Sending…' : 'Register interest'}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${isComparing ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}
                    onClick={() => toggleCompare(v.id)}
                    disabled={!isComparing && compareIds.length >= 2}>
                    {isComparing ? '✓ Comparing' : 'Compare'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Compare Sticky Bar */}
      {compareVendors.length === 2 && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: 'var(--surface)', borderTop: '2px solid var(--neon-cyan)', padding: '1.25rem 2rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700 }}>Comparing {compareVendors[0].name} vs {compareVendors[1].name}</div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCompareIds([])}>Clear comparison</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {compareVendors.map((v) => (
                <div key={v.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                  {[
                    { label: 'Grade', value: `Grade ${v.emissions_grade}`, color: GRADE_COLORS[v.emissions_grade] },
                    { label: 'CO2e reduction', value: `-${v.emission_reduction_pct}%`, color: 'var(--neon-green)' },
                    { label: 'Emissions score', value: v.emissions_score || '—' },
                    { label: 'Data visibility', value: v.scope3_visibility, color: VISIBILITY_COLORS[v.scope3_visibility] },
                  ].map((s) => (
                    <div key={s.label}>
                      <div className="stat-label">{s.label}</div>
                      <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', color: s.color || 'var(--text)', marginTop: '0.2rem', textTransform: 'capitalize' }}>{s.value}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
